import { useContext, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Theme, useMediaQuery, Checkbox, IconButton } from '@mui/material'

import { UserContext } from '../../contexts'
import { CustomerCompany } from '../../types'
import { numberWithCommas } from '../../utils'
import { countriesCurrencies } from '../../lib'
import SendIcon from '../../assets/icons/send-icon.svg'
import MailIcon from '../../assets/icons/mail-icon.svg'
import TrashIcon from '../../assets/icons/trash-icon.svg'
import AccountIcon from '../../assets/icons/account-icon.svg'
import ArrowRightIcon from '../../assets/icons/arrow-right-icon.svg'
import MoneyGreenIcon from '../../assets/icons/money-icon-green.svg'

const CustomerCompanyDataListItem = ({
  customerCompanyData,
  handleToggleCustomerCompanySelection,
  handleResendCustomerCompanyInvitation,
  handleOpenDeleteCustomerCompanyDataModal,
  handleOpenCustomerCompanyFundingDataModal
}: {
  customerCompanyData: CustomerCompany
  handleResendCustomerCompanyInvitation: (selectedCustomerCompanyData: CustomerCompany) => void
  handleOpenDeleteCustomerCompanyDataModal: (selectedCustomerCompanyData: CustomerCompany) => void
  handleOpenCustomerCompanyFundingDataModal: (selectedCustomerCompanyData: CustomerCompany) => void
  handleToggleCustomerCompanySelection: (selectedCustomerCompanyData: CustomerCompany, selected: boolean) => void
}) => {
  const navigate = useNavigate()
  const { userSession } = useContext(UserContext)
  const { t } = useTranslation(['customer-companies-management'])
  const xsUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('xs'))
  const lgUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('lg'))
  const mdUp = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'))
  const xsDown = useMediaQuery((theme: Theme) => theme.breakpoints.down('md'))

  const [selectedCurrency, setSelectedCurrency] = useState('COP')

  useEffect(() => {
    const currentSelectedCountry = globalThis.sessionStorage.getItem('selectedCountry')

    if (currentSelectedCountry) {
      setSelectedCurrency((countriesCurrencies as { [key: string]: string })[currentSelectedCountry])
      return
    }

    setSelectedCurrency('COP')
  }, [])

  return (
    <div
      onClick={() => {
        if (customerCompanyData?.activated === true) {
          navigate(`/customer-company-details/${String(customerCompanyData._id)}`)
        }
      }}
      className="payment-list-item-data-container"
    >
      <div className="payment-list-item-data-left-container company-customer-list-item-data-left-container">
        <div className="left-content">
          {(!userSession?.subRole ||
            (userSession?.subRole && userSession?.subRole !== 'payments-specialist' && userSession?.subRole !== 'reconciliation-specialist' && userSession?.subRole !== 'compliance-specialist')) && (
            <Checkbox
              checked={customerCompanyData.selected ?? false}
              onClick={(event) => event.stopPropagation()}
              onChange={(event) => {
                handleToggleCustomerCompanySelection(customerCompanyData, event.target.checked)
              }}
            />
          )}
          <div className="users-management-user-image-profile">
            <img
              src={AccountIcon}
              alt="account-icon"
            />
          </div>
          <div className="users-management-user-name-profile">{`${customerCompanyData?.businessCompany?.name?.toUpperCase()}`}</div>
        </div>
        {mdUp && (
          <div className="right-content">
            <div className="users-management-user-image-profile">
              <img
                src={MoneyGreenIcon}
                alt="money-icon"
              />
            </div>
            <div className="users-management-user-name-profile">{`$ ${numberWithCommas(Number(customerCompanyData?.balance).toFixed(2))} ${selectedCurrency}`}</div>
          </div>
        )}
      </div>
      <div className="payment-list-item-data-right-container company-customer-list-item-data-right-container">
        {lgUp && (
          <div className="left-content">
            <div className="users-management-user-image-profile">
              <img
                src={MailIcon}
                alt="mail-icon"
              />
            </div>
            {customerCompanyData?.businessCompany?.email ? (
              <div className="users-management-user-data-status customer-companies-management-customer-company-email">{customerCompanyData?.businessCompany?.email.toLowerCase()}</div>
            ) : (
              <div className="users-management-user-data-status customer-companies-management-customer-company-email">{customerCompanyData?.companyMainUserData?.email.toLowerCase()}</div>
            )}
          </div>
        )}
        <div
          className="right-content"
          style={
            userSession?.role === 'admin' ||
            (userSession?.role === 'super-admin' &&
              userSession?.subRole &&
              (userSession?.subRole === 'payments-specialist' || userSession?.subRole === 'reconciliation-specialist' || userSession?.subRole === 'compliance-specialist'))
              ? { justifyContent: 'flex-end', gap: '24px' }
              : {}
          }
        >
          {xsUp && customerCompanyData?.activated === false && (
            <div
              className="users-management-user-data-status customer-companies-management-customer-company-data-status"
              style={{ textAlign: 'center', color: customerCompanyData?.activated ? '#22C55E' : '#EF4444', width: '50%' }}
            >
              {customerCompanyData?.activated ? t('clientStatus.active') : t('clientStatus.inactive')}
            </div>
          )}
          {!xsDown && customerCompanyData?.activated === true && userSession?.role === 'super-admin' && (
            <>
              {(!userSession?.subRole ||
                (userSession?.subRole && userSession?.subRole !== 'payments-specialist' && userSession?.subRole !== 'reconciliation-specialist' && userSession?.subRole !== 'compliance-specialist')) && (
                <div
                  onClick={(event) => {
                    event.stopPropagation()
                    handleOpenCustomerCompanyFundingDataModal(customerCompanyData)
                  }}
                  className="users-management-user-data-status customer-companies-management-customer-company-funding-button-container"
                >
                  <div className="assign-balance-button-container customer-companies-management-customer-company-funding-button">{t('processFunding')}</div>
                </div>
              )}
            </>
          )}
          {xsDown && customerCompanyData?.activated === true && userSession?.role === 'super-admin' && (
            <>
              {(!userSession?.subRole ||
                (userSession?.subRole && userSession?.subRole !== 'payments-specialist' && userSession?.subRole !== 'reconciliation-specialist' && userSession?.subRole !== 'compliance-specialist')) && (
                <IconButton
                  edge="end"
                  sx={{ color: '#107569' }}
                  onClick={(event) => {
                    event.stopPropagation()
                    handleOpenCustomerCompanyFundingDataModal(customerCompanyData)
                  }}
                  aria-label="toggle password visibility"
                >
                  <img
                    src={MoneyGreenIcon}
                    alt="money-icon"
                  />
                </IconButton>
              )}
            </>
          )}
          {customerCompanyData?.activated === false && (
            <>
              {(!userSession?.subRole ||
                (userSession?.subRole && userSession?.subRole !== 'payments-specialist' && userSession?.subRole !== 'reconciliation-specialist' && userSession?.subRole !== 'compliance-specialist')) && (
                <IconButton
                  edge="end"
                  sx={{ color: '#107569' }}
                  onClick={(event) => {
                    event.stopPropagation()
                    handleResendCustomerCompanyInvitation(customerCompanyData)
                  }}
                  aria-label="toggle password visibility"
                >
                  <img
                    src={SendIcon}
                    alt="send-icon"
                  />
                </IconButton>
              )}
            </>
          )}
          {(!userSession?.subRole ||
            (userSession?.subRole && userSession?.subRole !== 'payments-specialist' && userSession?.subRole !== 'reconciliation-specialist' && userSession?.subRole !== 'compliance-specialist')) && (
            <IconButton
              edge="end"
              sx={{ color: '#107569' }}
              onClick={(event) => {
                event.stopPropagation()
                handleOpenDeleteCustomerCompanyDataModal(customerCompanyData)
              }}
              aria-label="toggle password visibility"
            >
              <img
                src={TrashIcon}
                alt="trash-icon"
              />
            </IconButton>
          )}
          <img
            src={ArrowRightIcon}
            alt="arrow-right-icon"
          />
        </div>
      </div>
    </div>
  )
}

export default CustomerCompanyDataListItem
